import { useState, useEffect, useRef } from "react";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Check, X, Link as LinkIcon, Gauge } from "lucide-react";

const DEFAULT_DURATION = 58; // seconds per loop (175% faster than the previous 102s)

/**
 * Reusable angled, fading, scrolling marquee band.
 *
 * - Tilt is derived from the measured container width so the text always sits
 *   parallel to the diagonal ("angle") cut it decorates, at every screen size.
 * - Admins edit inline (pencil): text, an optional link URL, how that link opens
 *   (same tab / new tab / in-page popup iframe), and the scroll speed.
 * - When a link is set, the whole strip becomes clickable for visitors.
 */
export const AngledMarquee = ({
  textField = "footer_marquee_text",
  enabledField = "footer_marquee_enabled",
  linkField = "footer_marquee_link_url",
  targetField = "footer_marquee_link_target",
  durationField = "footer_marquee_duration",
  outerClassName = "absolute inset-x-0 top-0 z-[2] h-[104px] overflow-hidden",
  textClassName = "text-white",
  textMarginTop = 38,
  editLabel = "Edit banner",
  testIds = {
    container: "footer-marquee",
    input: "marquee-input",
    linkInput: "marquee-link-input",
    target: "marquee-target",
    speed: "marquee-speed",
    save: "marquee-save",
    cancel: "marquee-cancel",
    edit: "marquee-edit",
    link: "marquee-link",
  },
}) => {
  const { settings, refresh } = useSite();
  const { user } = useAuth();
  const canEdit = !!user && user.role === "admin";
  const enabled = settings[enabledField] !== false;
  const text = settings[textField] || "";
  const linkUrl = (settings[linkField] || "").trim();
  const linkTarget = settings[targetField] || "_blank";
  const duration = Number(settings[durationField]) > 0 ? Number(settings[durationField]) : DEFAULT_DURATION;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [draftUrl, setDraftUrl] = useState(linkUrl);
  const [draftTarget, setDraftTarget] = useState(linkTarget);
  const [draftDuration, setDraftDuration] = useState(duration);
  const [saving, setSaving] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  // The parent diagonal drops a fixed number of px (clip depth) over the full
  // width, so its real angle changes as the screen scales. Measure and match it.
  const wrapRef = useRef(null);
  const [angle, setAngle] = useState(2.2);
  useEffect(() => {
    const compute = () => {
      const w = wrapRef.current?.offsetWidth || window.innerWidth || 1;
      const depth = w <= 640 ? 28 : 56; // must match the clip-path depth in index.css
      setAngle((Math.atan(depth / w) * 180) / Math.PI);
    };
    compute();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(compute) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener("resize", compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, []);

  useEffect(() => { setDraft(text); }, [text]);
  useEffect(() => { setDraftUrl(linkUrl); }, [linkUrl]);
  useEffect(() => { setDraftTarget(linkTarget); }, [linkTarget]);
  useEffect(() => { setDraftDuration(duration); }, [duration]);

  if ((!enabled || !text) && !canEdit) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        [textField]: draft,
        [enabledField]: true,
        [linkField]: draftUrl.trim(),
        [targetField]: draftTarget,
        [durationField]: Math.round(draftDuration),
      });
      await refresh();
      setEditing(false);
    } catch (e) {
      console.error("Marquee: failed to save settings", e);
      /* keep editor open on failure */
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setEditing(false);
    setDraft(text);
    setDraftUrl(linkUrl);
    setDraftTarget(linkTarget);
    setDraftDuration(duration);
  };

  const openLink = (e) => {
    if (!linkUrl) return;
    if (linkTarget === "popup") {
      e.preventDefault();
      setPopupOpen(true);
    } else if (linkTarget === "_self") {
      window.location.href = linkUrl;
    } else {
      window.open(linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  const unit = `${text}\u00A0\u00A0\u00A0·\u00A0\u00A0\u00A0`;
  const half = unit.repeat(8);

  return (
    <div
      ref={wrapRef}
      className={outerClassName}
      style={{ "--marquee-duration": `${duration}s` }}
      data-testid={testIds.container}
    >
      <div className="pointer-events-none select-none" style={{ transform: `rotate(${-angle}deg)`, transformOrigin: "center", marginTop: `${textMarginTop}px` }}>
        <div className="footer-marquee-mask overflow-hidden">
          <div className={`footer-marquee-track display-title text-lg ${textClassName}`}>
            <span>{half}</span>
            <span>{half}</span>
          </div>
        </div>
      </div>

      {/* Whole-strip clickable overlay for visitors when a link is configured */}
      {linkUrl && !editing && (
        <button
          onClick={openLink}
          data-testid={testIds.link}
          aria-label={text || "Open link"}
          className="pointer-events-auto absolute inset-0 z-[1] cursor-pointer"
        />
      )}

      {canEdit && (
        <button
          onClick={() => setEditing(true)}
          data-testid={testIds.edit}
          className="pointer-events-auto absolute right-3 top-2 z-[3] flex items-center gap-1 rounded-md border border-white/20 bg-black/70 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/80 backdrop-blur transition-colors hover:border-brand hover:text-white"
        >
          <Pencil className="h-3 w-3" /> {editLabel}
        </button>
      )}

      {/* Editor — rendered in a modal so it is never clipped by the marquee's
          overflow-hidden container. */}
      <Dialog open={editing} onOpenChange={(o) => { if (!o) cancel(); }}>
        <DialogContent className="max-w-md" data-testid={`${testIds.container}-editor`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Pencil className="h-4 w-4" /> {editLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Scrolling text</label>
              <Input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Scrolling text…"
                data-testid={testIds.input}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><LinkIcon className="h-3.5 w-3.5" /> Link URL (optional)</label>
              <Input
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                placeholder="https://…"
                data-testid={testIds.linkInput}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Link opens</label>
              <Select value={draftTarget} onValueChange={setDraftTarget}>
                <SelectTrigger data-testid={testIds.target}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_blank">Open in new window</SelectItem>
                  <SelectItem value="_self">Open in same window</SelectItem>
                  <SelectItem value="popup">Open in pop-up on page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Scroll speed</span>
                <span>{draftDuration}s / loop</span>
              </div>
              <Slider
                min={15}
                max={150}
                step={1}
                value={[draftDuration]}
                onValueChange={(v) => setDraftDuration(v[0])}
                data-testid={testIds.speed}
              />
              <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
                <span>Faster</span><span>Slower</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={cancel} data-testid={testIds.cancel} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /> Cancel</button>
            <button onClick={save} disabled={saving} data-testid={testIds.save} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Save</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Popup modal (in-page iframe). Kept mounted so Radix can fully clean up
          its overlay/pointer-lock on close; content only loads while open. */}
      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent className="h-[80vh] max-w-4xl p-0 sm:p-0" data-testid={`${testIds.container}-popup`}>
          <DialogHeader className="border-b border-border px-4 py-3">
            <DialogTitle className="truncate text-sm">{text || linkUrl}</DialogTitle>
          </DialogHeader>
          {popupOpen && linkUrl && (
            <iframe
              src={linkUrl}
              title={text || "Embedded content"}
              className="h-full w-full rounded-b-lg border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
